/**
 * Unified API Response Utilities (Backoffice)
 *
 * Consolidates all API response handling patterns across the backoffice app.
 * Mirrors wingman/src/utils/api-response.ts for consistency.
 */
// ============================================================================
// Response Unwrapping Utilities
// ============================================================================
export function unwrapApiResponse(response) {
    if (response && typeof response === 'object' && 'data' in response) {
        const data = response.data;
        if (data && typeof data === 'object' && 'data' in data) {
            const nestedData = data.data;
            if (nestedData && typeof nestedData === 'object' && 'data' in nestedData) {
                return nestedData.data;
            }
            return nestedData;
        }
        return data;
    }
    return response;
}
export function unwrapApiResponseOr(response, defaultValue) {
    try {
        const unwrapped = unwrapApiResponse(response);
        return unwrapped ?? defaultValue;
    }
    catch {
        return defaultValue;
    }
}
// ============================================================================
// Response Transformation Utilities
// ============================================================================
/**
 * Convert snake_case string to camelCase
 */
function snakeToCamel(str) {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}
/**
 * Recursively transform object keys from snake_case to camelCase
 */
export function transformResponse(data, options) {
    const { preserveKeys = [] } = options || {};
    if (data === null || data === undefined) {
        return data;
    }
    if (Array.isArray(data)) {
        return data.map((item) => transformResponse(item, options));
    }
    if (typeof data === 'object' && data.constructor === Object) {
        const transformed = {};
        for (const [key, value] of Object.entries(data)) {
            const transformedKey = preserveKeys.includes(key) ? key : snakeToCamel(key);
            transformed[transformedKey] = transformResponse(value, options);
        }
        return transformed;
    }
    return data;
}
export function unwrapAndTransform(response, options) {
    const unwrapped = unwrapApiResponse(response);
    return transformResponse(unwrapped, options);
}
// ============================================================================
// Pagination Utilities
// ============================================================================
export function unwrapPaginatedResponse(response, options) {
    const { dataKey = 'data', transformData = false } = options || {};
    const unwrapped = unwrapApiResponse(response);
    let data;
    let paginationRaw;
    if (unwrapped && typeof unwrapped === 'object') {
        data = unwrapped[dataKey] || [];
        paginationRaw = unwrapped.pagination || {};
    }
    else {
        data = [];
        paginationRaw = {};
    }
    if (transformData) {
        data = data.map((item) => transformResponse(item));
    }
    const pagination = {
        page: paginationRaw.page || 1,
        limit: paginationRaw.limit || paginationRaw.perPage || paginationRaw.per_page || 10,
        total: paginationRaw.total || 0,
        totalPages: paginationRaw.totalPages ||
            paginationRaw.total_pages ||
            paginationRaw.pages ||
            Math.ceil((paginationRaw.total || 0) / (paginationRaw.limit || paginationRaw.perPage || 10)),
    };
    return { data, pagination };
}
export function createPaginatedResponse(data, page, limit, total) {
    return {
        data,
        pagination: {
            page,
            limit,
            total,
            totalPages: Math.ceil(total / limit),
        },
    };
}
// ============================================================================
// Response Builder Utilities
// ============================================================================
export function createSuccessResponse(data) {
    return {
        success: true,
        data,
    };
}
export function createErrorResponse(code, message, details) {
    return {
        success: false,
        error: {
            code,
            message,
            ...(details && { details }),
        },
    };
}
export class ApiResponseBuilder {
    static success(data) {
        return createSuccessResponse(data);
    }
    static error(code, message, details) {
        return createErrorResponse(code, message, details);
    }
    static paginated(data, page, limit, total) {
        return {
            success: true,
            data: createPaginatedResponse(data, page, limit, total),
        };
    }
    static unwrapSuccess(response) {
        return createSuccessResponse(unwrapApiResponse(response));
    }
    static unwrapTransformSuccess(response, options) {
        return createSuccessResponse(unwrapAndTransform(response, options));
    }
}
